<template>
  <owal-event-banner
    :poster-frame="posterFrame"
    :full-frame="fullFrame"
    :unix-epoch-milliseconds="unixTimestamp"
    :dot-color="dotColor"
    :class="{dismissed}"
  >
    <div
      slot="content"
      class="owal-elopement-alerts__slot-container"
    >
      <div
        class="owal-elopement-alerts__description"
      >
        <h2
          class="owal-elopement-alerts__title"
        >
          {{ `${nameForFaceUUID(frontendFaceUUID)}` }}
        </h2>
        <h3
          class="owal-elopement-alerts__subtitle"
        >
          {{ `${cameraDescription}` }}
        </h3>
        <div class="owal-elopement-alerts__bottom">
          <p>{{ minutesAgo }}</p>
          <div class="button-box">
            <owal-banner-button
              v-if="!dismissed"
              type="static"
              title="Dismiss"
              accessory-type="none"
              identifier="button"
              @click="handleClick(alertId)"
            />
          </div>
        </div>
      </div>
    </div>
  </owal-event-banner>
</template>

<script>
import OwalEventBanner from '@/libraries/owal-ui/banners/OwalEventBanner.vue'
import OwalBannerButton from '@/libraries/owal-ui/buttons/OwalBannerButton.vue'
import moment from 'moment'
import { playSound } from '@/libraries/util/chime'
import interpolate from 'color-interpolate'

export default {
  name: 'ElopementAlertBanner',
  components: {
    OwalEventBanner,
    OwalBannerButton,
  },
  props: {
    now: Date,
    unixTimestamp: Number,
    cameraDescription: String,
    posterFrame: String,
    fullFrame: String,
    frontendFaceUUID: String,
    alertId: String,
    dismissedAt: String,
  },
  computed: {
    minutesAgo () {
      const now = moment(this.now)
      const timeAgo = moment(this.unixTimestamp).from(now)
      return timeAgo
    },
    dismissed () {
      return this.dismissedAt !== null
    },
    /**
     * This method returns a color for the dot based on the age of an event.
     * Here's how it works:
     *
     * 1. We define an array of colors.
     * 2. Compute how many milliseconds have elapsed since this alert.
     * 3. Compute how many milliseconds have elapsed since 24 hours ago.
     * 4. Divide the milliseconds since event by milliseconds since 24 hours ago. (This gives us a ratio between 0 and 1.)
     * 5. Use the ratio to map to a color.
     *
     * The colors it uses are:
     * red (#ff5252)
     * orange (#ff7c00)
     * yellow (#ffc935)
     * blue (#00c0bf)
     * gray (#808080)
     */
    dotColor () {
      // 1. define colors
      const colormap = interpolate([
        '#ff5252',
        '#ff7c00',
        '#ffc935',
        '#00c0bf',
        '#808080',
      ])

      // 2. milliseconds since alert
      const timeBetweenNowAndAlert = moment().valueOf() - this.unixTimestamp

      // 3. milliseconds since 24 hr ago
      const aDayAgo =
        this.unixTimestamp -
        moment()
          .subtract(24, 'hours')
          .valueOf()

      // 4. divide
      const percentage = timeBetweenNowAndAlert / aDayAgo

      // 5. map to color.
      const color = colormap(percentage)
      return color
    },
  },
  created () {
    playSound()
    window.setInterval(this.$forceUpdate(), 5000)
  },
  methods: {
    nameForFaceUUID (uuid) {
      const face = this.$store.getters.faceForUUID(uuid)
      const name = face ? face.person_name : 'Unknown Person'
      return name.replace(' ', '\xa0') // use nbsp for names
    },
    handleClick (alertId) {
      this.$emit('click', alertId)
    },
  },
}
</script>

<style>
/** The slot content container */
.owal-elopement-alerts__slot-container {
  padding-left: 1.5rem;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
}

.owal-elopement-alerts__description {
  flex-direction: column;
  justify-content: center;
  width: 100%;
  /*min-width: 18.2rem; !* *!*/
}

.button-box {
  align-self: center;
  margin-right: 2%;
  margin-left: auto;
  display: flex;
  flex-shrink: 0;
}

/** The title of the alert. */
.owal-elopement-alerts__title {
  margin-bottom: 0;
  font-weight: bolder;
  font-size: 1.65rem;
  opacity: 0.85;
}

/** The subtitle of the alert. */
.owal-elopement-alerts__subtitle {
  margin-top: 0;
  margin-bottom: 0;
  font-size: 1.2rem;
}

/** The bottom row of the alert. */
.owal-elopement-alerts__bottom {
  display: flex;
}

/** Indicate dismissed alerts */
.dismissed {
  opacity: 0.5;
  transition: all 300ms ease;
}

.dismissed:hover {
  opacity: 1;
}
</style>
